import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FiArrowLeft, FiPlus, FiX, FiTrash2, FiEdit2, FiUsers, FiUserPlus, FiSettings, FiCheck } from 'react-icons/fi';

const COLUMNS = [
  { key: 'To Do', label: 'To Do', color: '#64748b', bg: 'rgba(100,116,139,0.06)' },
  { key: 'Planned', label: 'Planned', color: '#8b5cf6', bg: 'rgba(139,92,246,0.06)' },
  { key: 'In Progress', label: 'In Progress', color: '#2563eb', bg: 'rgba(37,99,235,0.06)' },
  { key: 'Complete', label: 'Complete', color: '#10b981', bg: 'rgba(16,185,129,0.06)' },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authFetch, user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingTo, setAddingTo] = useState(null); // column key
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDesc, setNewItemDesc] = useState('');
  const [dragItem, setDragItem] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', status: '', deadline: '' });
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const dragRef = useRef(null);
  const [dragConfirm, setDragConfirm] = useState(null); // { item, targetStatus }

  const loadProject = () => {
    authFetch(`/api/projects/${id}`).then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setProject(data);
          setEditForm({ name: data.name, description: data.description || '', status: data.status, deadline: data.deadline || '' });
        }
        setLoading(false);
      }).catch(() => setLoading(false));
  };

  useEffect(() => { loadProject(); }, [id]);

  const handleAddItem = async (status) => {
    if (!newItemTitle.trim()) return;
    const res = await authFetch(`/api/projects/${id}/items`, {
      method: 'POST',
      body: JSON.stringify({ title: newItemTitle.trim(), description: newItemDesc.trim(), status })
    });
    if (res.ok) {
      setNewItemTitle('');
      setNewItemDesc('');
      setAddingTo(null);
      loadProject();
    }
  };

  const handleDeleteItem = async (itemId) => {
    const res = await authFetch(`/api/projects/${id}/items/${itemId}`, { method: 'DELETE' });
    if (res.ok) loadProject();
  };

  const handleUpdateItem = async (itemId, updates) => {
    const res = await authFetch(`/api/projects/${id}/items/${itemId}`, { method: 'PUT', body: JSON.stringify(updates) });
    if (res.ok) loadProject();
  };

  const handleDragStart = (e, item) => {
    setDragItem(item);
    dragRef.current = item;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
  };

  const handleDragOver = (e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(colKey);
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    const item = dragRef.current;
    if (!item || item.status === targetStatus) { setDragItem(null); return; }
    setDragItem(null);
    setDragConfirm({ item, targetStatus });
  };

  const confirmDragMove = async () => {
    if (!dragConfirm) return;
    const { item, targetStatus } = dragConfirm;

    // Optimistic update
    setProject(prev => ({
      ...prev,
      items: prev.items.map(i => i.id === item.id ? { ...i, status: targetStatus } : i)
    }));

    // Update on server
    await authFetch(`/api/projects/${id}/items/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: targetStatus })
    });
    setDragConfirm(null);
    loadProject();
  };

  const handleSaveSettings = async () => {
    const res = await authFetch(`/api/projects/${id}`, { method: 'PUT', body: JSON.stringify(editForm) });
    if (res.ok) { showToast('Project updated'); setShowSettings(false); loadProject(); }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Delete this project? All items must be removed first.')) return;
    const res = await authFetch(`/api/projects/${id}`, { method: 'DELETE' });
    if (res.ok) navigate('/projects');
    else { const d = await res.json().catch(() => ({})); showToast(d.error || 'Failed to delete', 'error'); }
  };

  const handleAddMember = async (userId, role) => {
    const res = await authFetch(`/api/projects/${id}/members`, { method: 'POST', body: JSON.stringify({ user_id: userId, role }) });
    if (res.ok) { loadProject(); setMemberSearch(''); }
  };

  const handleRemoveMember = async (userId) => {
    const res = await authFetch(`/api/projects/${id}/members/${userId}`, { method: 'DELETE' });
    if (res.ok) loadProject();
  };

  if (loading) return (
    <div>
      <div className="skeleton" style={{ width: 200, height: 28, marginBottom: 16 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 300, borderRadius: 12 }} />)}
      </div>
    </div>
  );

  if (!project) return (
    <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
      <div style={{ fontSize: 15 }}>Project not found</div>
      <button onClick={() => navigate('/projects')} style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>Back to Projects</button>
    </div>
  );

  const items = project.items || [];
  const totalItems = items.length;
  const completedItems = items.filter(i => i.status === 'Complete').length;
  const pct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  const canEdit = project.canManage || project.myRole === 'contributor';

  return (
    <div>
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: 'fixed', top: 20, right: 20, zIndex: 999, padding: '12px 20px', borderRadius: 10,
              background: toast.type === 'error' ? '#dc2626' : '#16a34a', color: '#fff', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/projects')}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
          <FiArrowLeft size={14} /> Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {project.tag && <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: '#2563eb', background: 'rgba(37,99,235,0.08)', padding: '2px 8px', borderRadius: 4 }}>{project.tag}</span>}
            <h1 style={{ margin: 0, fontSize: 'clamp(18px, 4vw, 24px)', fontWeight: 800, color: 'var(--text-primary)' }}>{project.name}</h1>
          </div>
          {project.description && <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>{project.description}</p>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ textAlign: 'center', padding: '4px 12px', borderRadius: 8, background: pct === 100 ? 'rgba(16,185,129,0.08)' : 'rgba(37,99,235,0.08)' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: pct === 100 ? '#10b981' : '#2563eb' }}>{pct}%</div>
            <div style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 600 }}>{completedItems}/{totalItems}</div>
          </div>
          {project.canManage && (
            <>
              <button onClick={() => { setShowAddMember(true); authFetch('/api/users').then(r => r.ok ? r.json() : []).then(setAllUsers).catch(() => {}); }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
                <FiUsers size={14} /> Members
              </button>
              <button onClick={() => setShowSettings(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
                <FiSettings size={14} />
              </button>
            </>
          )}
        </div>
      </motion.div>

      {/* Progress bar */}
      <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 2, background: pct === 100 ? '#10b981' : '#2563eb', width: `${pct}%`, transition: 'width 0.3s' }} />
      </div>

      {/* Kanban Board */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, minHeight: 400 }}>
        {COLUMNS.map(col => {
          const colItems = items.filter(i => i.status === col.key).sort((a, b) => a.sort_order - b.sort_order);
          const isDragOver = dragOverCol === col.key;
          return (
            <div key={col.key}
              onDragOver={e => handleDragOver(e, col.key)}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={e => handleDrop(e, col.key)}
              style={{
                background: isDragOver ? `${col.color}12` : col.bg,
                borderRadius: 12, padding: 12, border: `2px ${isDragOver ? 'solid' : 'dashed'} ${isDragOver ? col.color + '60' : 'transparent'}`,
                transition: 'all 0.2s', display: 'flex', flexDirection: 'column'
              }}>
              {/* Column header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: col.color }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{col.label}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '0 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>{colItems.length}</span>
                </div>
                {canEdit && (
                  <button onClick={() => { setAddingTo(col.key); setNewItemTitle(''); setNewItemDesc(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: col.color, padding: 2 }}>
                    <FiPlus size={16} />
                  </button>
                )}
              </div>

              {/* Cards */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {colItems.map(item => (
                  <motion.div key={item.id}
                    draggable={canEdit}
                    onDragStart={e => canEdit ? handleDragStart(e, item) : e.preventDefault()}
                    onDragEnd={() => { setDragItem(null); setDragOverCol(null); }}
                    whileHover={{ y: -2 }}
                    onClick={() => setViewItem(item)}
                    style={{
                      padding: '10px 12px', borderRadius: 8, background: 'var(--bg-card)',
                      border: '1px solid var(--border)', cursor: canEdit ? 'grab' : 'pointer',
                      opacity: dragItem?.id === item.id ? 0.5 : 1,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'opacity 0.15s'
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{item.title}</div>
                    {item.description && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{item.description}</div>
                    )}
                    {item.assigned_to_name && (
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiUsers size={10} /> {item.assigned_to_name}
                      </div>
                    )}
                  </motion.div>
                ))}

                {/* Add item inline */}
                {addingTo === col.key && (
                  <div style={{ padding: 10, borderRadius: 8, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                    <input value={newItemTitle} onChange={e => setNewItemTitle(e.target.value)} autoFocus
                      placeholder="Item title..."
                      onKeyDown={e => { if (e.key === 'Enter' && newItemTitle.trim()) handleAddItem(col.key); if (e.key === 'Escape') setAddingTo(null); }}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', marginBottom: 6, boxSizing: 'border-box' }} />
                    <textarea value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)}
                      placeholder="Description (optional)"
                      rows={2}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11, outline: 'none', resize: 'none', fontFamily: 'inherit', marginBottom: 6, boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button onClick={() => setAddingTo(null)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
                      <button onClick={() => handleAddItem(col.key)} disabled={!newItemTitle.trim()}
                        style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: col.color, color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, opacity: newItemTitle.trim() ? 1 : 0.5 }}>Add</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* View Item Modal */}
      <AnimatePresence>
        {viewItem && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => { setViewItem(null); setEditItem(null); }}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 480, border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              {editItem ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Edit Item</h3>
                    <button onClick={() => setEditItem(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <input value={editItem.title} onChange={e => setEditItem(ei => ({ ...ei, title: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, outline: 'none', boxSizing: 'border-box' }} />
                    <textarea value={editItem.description || ''} onChange={e => setEditItem(ei => ({ ...ei, description: e.target.value }))}
                      placeholder="Description..." rows={4}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    <select value={editItem.status} onChange={e => setEditItem(ei => ({ ...ei, status: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}>
                      {COLUMNS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                    <button onClick={() => setEditItem(null)}
                      style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={async () => {
                        await handleUpdateItem(editItem.id, { title: editItem.title, description: editItem.description, status: editItem.status });
                        setEditItem(null); setViewItem(null);
                      }}
                      style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      Save
                    </motion.button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{viewItem.title}</div>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                        background: COLUMNS.find(c => c.key === viewItem.status)?.bg || '#f1f5f9',
                        color: COLUMNS.find(c => c.key === viewItem.status)?.color || '#64748b'
                      }}>{viewItem.status}</span>
                    </div>
                    <button onClick={() => { setViewItem(null); setEditItem(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                  </div>
                  {viewItem.description && (
                    <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, marginBottom: 12, whiteSpace: 'pre-wrap' }}>
                      {viewItem.description}
                    </div>
                  )}
                  {viewItem.assigned_to_name && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Assigned to: <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{viewItem.assigned_to_name}</span></div>
                  )}
                  {canEdit && (
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={() => { handleDeleteItem(viewItem.id); setViewItem(null); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        <FiTrash2 size={12} /> Delete
                      </button>
                      <button onClick={() => setEditItem({ ...viewItem })}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        <FiEdit2 size={12} /> Edit
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Members Modal */}
      <AnimatePresence>
        {showAddMember && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setShowAddMember(false)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '80vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Project Members</h3>
                <button onClick={() => setShowAddMember(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>

              {/* Current members */}
              {(project.members || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Current Members</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {project.members.map(m => (
                      <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.avatar_color || '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                          {(() => { const p = (m.name || '').trim().split(/\s+/); return p.length >= 2 ? (p[0][0] + '.' + p[p.length-1][0]).toUpperCase() : (p[0]?.[0] || '?').toUpperCase(); })()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{m.name}</div>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                            background: m.role === 'owner' ? 'rgba(245,158,11,0.08)' : m.role === 'moderator' ? 'rgba(37,99,235,0.08)' : m.role === 'contributor' ? 'rgba(16,185,129,0.08)' : 'rgba(100,116,139,0.08)',
                            color: m.role === 'owner' ? '#f59e0b' : m.role === 'moderator' ? '#2563eb' : m.role === 'contributor' ? '#10b981' : '#64748b'
                          }}>{m.role === 'viewer' ? 'view only' : m.role}</span>
                        </div>
                        {m.role !== 'owner' ? (
                          <>
                            <select value={m.role} onChange={e => handleAddMember(m.user_id, e.target.value)}
                              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 11, outline: 'none' }}>
                              <option value="moderator">Moderator</option>
                              <option value="contributor">Contributor</option>
                              <option value="viewer">View Only</option>
                            </select>
                            <button onClick={() => handleRemoveMember(m.user_id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 4 }}>
                              <FiX size={14} />
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>Creator</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add member */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Add Member</div>
                <input value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search users..."
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', marginBottom: 8, boxSizing: 'border-box' }} />
                {memberSearch.trim() && (() => {
                  const memberIds = new Set((project.members || []).map(m => m.user_id));
                  const filtered = allUsers.filter(u => !u.disabled && !memberIds.has(u.id) && (u.name?.toLowerCase().includes(memberSearch.toLowerCase()) || u.email?.toLowerCase().includes(memberSearch.toLowerCase())));
                  return filtered.length > 0 ? (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 200, overflowY: 'auto' }}>
                      {filtered.slice(0, 10).map(u => (
                        <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                          onClick={() => handleAddMember(u.id, 'viewer')}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--border-subtle)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>{u.email}</span>
                          <FiUserPlus size={14} color="var(--accent)" />
                        </div>
                      ))}
                    </div>
                  ) : <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No matching users</div>;
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setShowSettings(false)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Project Settings</h3>
                <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Name</label>
                  <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
                  <textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                    rows={3}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Status</label>
                  <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}>
                    <option value="Active">Active</option>
                    <option value="Complete">Complete</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Deadline</label>
                  <input type="date" value={editForm.deadline} onChange={e => setEditForm(f => ({ ...f, deadline: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
                <button onClick={handleDeleteProject}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  <FiTrash2 size={12} /> Delete Project
                </button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowSettings(false)}
                    style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={handleSaveSettings}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    Save
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Drag Confirm Modal */}
      <AnimatePresence>
        {dragConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setDragConfirm(null)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Move Item</h3>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
                Move <strong>"{dragConfirm.item.title}"</strong> from <strong>{dragConfirm.item.status}</strong> to <strong>{dragConfirm.targetStatus}</strong>?
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button onClick={() => setDragConfirm(null)}
                  style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={confirmDragMove}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  Confirm Move
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
